import { useEffect, useState } from 'react';
import type { ModelAsset } from '@/content/schema';
import { MODELS_BASE } from '@/hero/preload';
import { net } from '@/lib/net';
import { hero } from '@/state/tour';

declare const __TEXTURE_CREDITS__: string;

type Credit = NonNullable<ModelAsset['credit']>;

const LIBRARIES = [
  'phi-ag/rvt · Peter Hirn · MIT',
  'Revit basic sample project · Autodesk · sample content',
  'ifc-lite · MPL-2.0',
  'three.js · MIT',
  '© OpenStreetMap contributors · ODbL',
];

const line = (c: Credit) => `${c.title.replace(/\s*\(.*\)$/, '')} · ${c.author} · ${c.licence}`;

// list items of site/public/textures/CREDITS.md, read at build time
function textureCredits(): string[] {
  return __TEXTURE_CREDITS__
    .split('\n')
    .map((l) => l.match(/^\s*[-*]\s+(.+)$/)?.[1])
    .filter((l): l is string => !!l)
    .map((l) => l.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').trim());
}

// model credits from manifest.json where the pipeline wrote one, the tour's layer credit otherwise
export function useCredits(): string[] {
  const [manifest, setManifest] = useState<Record<string, { credit?: Credit }>>({});
  useEffect(() => {
    let live = true;
    net(MODELS_BASE + 'manifest.json')
      .then((r) => (r.ok ? r.json() : {}))
      .then((m: Record<string, { credit?: Credit }>) => live && setManifest(m))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  const models = hero.layers
    .map((l) => manifest[l.id]?.credit ?? l.credit)
    .filter((c): c is Credit => !!c)
    .map(line);
  return [...new Set([...models, ...textureCredits(), ...LIBRARIES])];
}
